/**
 * 30-second turn clock & 3-consecutive own-turn timeout state reducer.
 * - Pure deterministic transition reducer shared by authoritative PvP server and local match controller.
 * - nowMs on PvP must always be SERVER time (never client clock).
 * - Automatic timeout passes reset manual pass streak and never overwrite lastCommittedMove.
 */
import type { Side } from './JanggiRules.js';

export type { Side };
export const TURN_MS = 30_000;
export const IDLE_LIMIT = 3;

export interface TurnToken {
  matchId: string;
  epoch: number;
  turnId: number;
  generation: number;
  side: Side;
}

export interface TurnClock {
  token: TurnToken;
  status: 'running' | 'paused' | 'finished';
  turnDurationMs: number;
  startedAtMs: number;
  deadlineAtMs: number | null;
  pausedRemainingMs: number | null;
  idleStreak: Record<Side, number>;
}

export type ClockOutcome = {
  clock: TurnClock;
  kind:
    | 'ignored'
    | 'rejected'
    | 'move'
    | 'manual_pass'
    | 'timeout_pass'
    | 'idle_forfeit'
    | 'time_forfeit_in_check'
    | 'paused'
    | 'resumed'
    | 'finished';
  side?: Side;
  winner?: Side;
  reason?: string;
  manualPassStreakEffect?: 'existing_manual_rule' | 'reset';
};

function other(side: Side): Side {
  return side === 'cho' ? 'han' : 'cho';
}

function assertNow(nowMs: number): void {
  if (!Number.isFinite(nowMs) || nowMs < 0) {
    throw new RangeError('Invalid server clock');
  }
}

export function sameToken(a: TurnToken, b: TurnToken): boolean {
  return (
    a.matchId === b.matchId &&
    a.epoch === b.epoch &&
    a.turnId === b.turnId &&
    a.generation === b.generation &&
    a.side === b.side
  );
}

export function createTurnClock(
  matchId: string,
  epoch: number,
  side: Side,
  nowMs: number,
  turnDurationMs: number = TURN_MS
): TurnClock {
  assertNow(nowMs);
  if (!matchId || !Number.isInteger(epoch) || epoch < 1) {
    throw new RangeError('Invalid match identity');
  }
  const dur = Number.isFinite(turnDurationMs) && turnDurationMs > 0 ? Math.floor(turnDurationMs) : TURN_MS;
  return {
    token: { matchId, epoch, turnId: 0, generation: 0, side },
    status: 'running',
    turnDurationMs: dur,
    startedAtMs: nowMs,
    deadlineAtMs: nowMs + dur,
    pausedRemainingMs: null,
    idleStreak: { cho: 0, han: 0 }
  };
}

export function remainingMs(clock: TurnClock, nowMs: number): number {
  assertNow(nowMs);
  if (clock.status === 'finished') return 0;
  if (clock.status === 'paused') return Math.max(0, clock.pausedRemainingMs ?? 0);
  return Math.max(0, (clock.deadlineAtMs ?? nowMs) - nowMs);
}

function nextTurn(clock: TurnClock, nowMs: number, idleStreak: Record<Side, number>): TurnClock {
  const dur = clock.turnDurationMs || TURN_MS;
  return {
    ...clock,
    token: {
      ...clock.token,
      side: other(clock.token.side),
      turnId: clock.token.turnId + 1,
      generation: clock.token.generation + 1
    },
    status: 'running',
    turnDurationMs: dur,
    startedAtMs: nowMs,
    deadlineAtMs: nowMs + dur,
    pausedRemainingMs: null,
    idleStreak
  };
}

export function finishClock(clock: TurnClock): TurnClock {
  return {
    ...clock,
    status: 'finished',
    deadlineAtMs: null,
    pausedRemainingMs: null,
    token: { ...clock.token, generation: clock.token.generation + 1 }
  };
}

/** Exactly-at-deadline is expired. Only this transition can emit an automatic pass. */
export function expireTurn(
  clock: TurnClock,
  expected: TurnToken,
  nowMs: number,
  inCheck: boolean
): ClockOutcome {
  assertNow(nowMs);
  if (!sameToken(clock.token, expected) || clock.status !== 'running') {
    return { clock, kind: 'ignored', reason: 'stale_or_inactive_timer' };
  }
  if (clock.deadlineAtMs === null || nowMs < clock.deadlineAtMs) {
    return { clock, kind: 'ignored', reason: 'not_due' };
  }
  const side = clock.token.side;
  const idleStreak = { ...clock.idleStreak, [side]: clock.idleStreak[side] + 1 };
  if (idleStreak[side] >= IDLE_LIMIT) {
    return {
      clock: finishClock({ ...clock, idleStreak }),
      kind: 'idle_forfeit',
      side,
      winner: other(side),
      reason: 'three_consecutive_own_turn_timeouts'
    };
  }
  // Explicit check exception: never create an illegal pass while in check.
  // Uses time_forfeit_in_check, NOT checkmate (the player may have had legal evasions).
  if (inCheck) {
    return {
      clock: finishClock({ ...clock, idleStreak }),
      kind: 'time_forfeit_in_check',
      side,
      winner: other(side),
      reason: 'cannot_pass_in_check'
    };
  }
  return {
    clock: nextTurn(clock, nowMs, idleStreak),
    kind: 'timeout_pass',
    side,
    manualPassStreakEffect: 'reset'
  };
}

/** Rejected moves, selections, hints and opening menus must never reset this clock. */
export function commitLegalAction(
  clock: TurnClock,
  expected: TurnToken,
  side: Side,
  nowMs: number,
  action: 'move' | 'manual_pass',
  legal: boolean,
  inCheck: boolean
): ClockOutcome {
  assertNow(nowMs);
  if (!sameToken(clock.token, expected) || clock.status !== 'running' || side !== clock.token.side) {
    return { clock, kind: 'rejected', reason: 'stale_or_wrong_turn' };
  }
  if (clock.deadlineAtMs !== null && nowMs >= clock.deadlineAtMs) {
    return expireTurn(clock, expected, nowMs, inCheck);
  }
  if (!legal || (action === 'manual_pass' && inCheck)) {
    return { clock, kind: 'rejected', reason: 'illegal_action' };
  }
  const idleStreak = { ...clock.idleStreak, [side]: 0 };
  return {
    clock: nextTurn(clock, nowMs, idleStreak),
    kind: action,
    side,
    manualPassStreakEffect: action === 'manual_pass' ? 'existing_manual_rule' : 'reset'
  };
}

/** Only a server-confirmed transport recovery can pause; document.hidden cannot. */
export function pauseForReconnect(
  clock: TurnClock,
  expected: TurnToken,
  nowMs: number,
  inCheck: boolean
): ClockOutcome {
  assertNow(nowMs);
  if (!sameToken(clock.token, expected) || clock.status !== 'running') {
    return { clock, kind: 'ignored', reason: 'stale_or_inactive_pause' };
  }
  if (clock.deadlineAtMs !== null && nowMs >= clock.deadlineAtMs) {
    return expireTurn(clock, expected, nowMs, inCheck);
  }
  return {
    kind: 'paused',
    clock: {
      ...clock,
      status: 'paused',
      pausedRemainingMs: remainingMs(clock, nowMs),
      deadlineAtMs: null,
      token: { ...clock.token, generation: clock.token.generation + 1 }
    }
  };
}

export function resumeAfterReconnect(
  clock: TurnClock,
  expected: TurnToken,
  nowMs: number,
  recovery: {
    allRequiredPeersConnected: boolean;
    graceDeadlineAtMs: number;
  }
): ClockOutcome {
  assertNow(nowMs);
  if (!sameToken(clock.token, expected) || clock.status !== 'paused') {
    return { clock, kind: 'ignored', reason: 'stale_or_inactive_resume' };
  }
  if (!recovery.allRequiredPeersConnected) {
    return { clock, kind: 'ignored', reason: 'peer_still_recovering' };
  }
  if (!Number.isFinite(recovery.graceDeadlineAtMs) || nowMs >= recovery.graceDeadlineAtMs) {
    return { clock, kind: 'ignored', reason: 'recovery_grace_expired' };
  }
  const rem = Math.max(0, clock.pausedRemainingMs ?? 0);
  const dur = clock.turnDurationMs || TURN_MS;
  return {
    kind: 'resumed',
    clock: {
      ...clock,
      status: 'running',
      startedAtMs: nowMs - Math.max(0, dur - rem),
      deadlineAtMs: nowMs + rem,
      pausedRemainingMs: null,
      token: { ...clock.token, generation: clock.token.generation + 1 }
    }
  };
}

/**
 * Restores turn clock on consensus undo without clearing idleStreak or granting free 30s refills.
 */
export function restoreClockOnUndo(
  clock: TurnClock,
  restoredSide: Side,
  savedRemainingMs: number,
  nowMs: number
): TurnClock {
  assertNow(nowMs);
  const dur = clock.turnDurationMs || TURN_MS;
  const rawSaved = Number.isFinite(savedRemainingMs) && savedRemainingMs > 0 ? Math.floor(savedRemainingMs) : dur;
  const clampedBudget = Math.min(dur, Math.max(1, rawSaved));
  if (clock.status === 'paused') {
    return {
      ...clock,
      token: {
        ...clock.token,
        side: restoredSide,
        turnId: clock.token.turnId + 1,
        generation: clock.token.generation + 1
      },
      pausedRemainingMs: clampedBudget,
      deadlineAtMs: null
    };
  }
  return {
    ...clock,
    status: 'running',
    token: {
      ...clock.token,
      side: restoredSide,
      turnId: clock.token.turnId + 1,
      generation: clock.token.generation + 1
    },
    startedAtMs: nowMs - Math.max(0, dur - clampedBudget),
    deadlineAtMs: nowMs + clampedBudget,
    pausedRemainingMs: null
  };
}

/**
 * Safely freezes an active turn clock when recovering persisted state after a server restart,
 * preventing server downtime from fabricating 3 consecutive AFK timeouts or granting a free full 30s.
 */
export function freezeClockAfterServerRestart(clock: TurnClock, savedAtMs: number): TurnClock {
  if (clock.status !== 'running') return clock;
  const dur = clock.turnDurationMs || TURN_MS;
  const safeNow = Number.isFinite(savedAtMs) && savedAtMs > 0 ? savedAtMs : clock.startedAtMs;
  const rem =
    clock.deadlineAtMs !== null
      ? Math.min(dur, Math.max(1, Math.floor(clock.deadlineAtMs - safeNow)))
      : dur;
  return {
    ...clock,
    status: 'paused',
    deadlineAtMs: null,
    pausedRemainingMs: rem,
    token: { ...clock.token, generation: clock.token.generation + 1 }
  };
}
